import { get, noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import Button from '@doordash/component-button'
import { ErrorMessage, Select, TextField } from '@doordash/component-fields'
import { Stack, StackChildren, Text } from '@doordash/design-language'
import { CANADA_PROVINCE_CODES, STATE_CODES } from '../../../constants'

import * as actions from '../../../actions'
import * as events from '../../../events'

import { isSubmittingAddressSelector } from '../../../selectors'
import DDToCheckrModal from '../dd-to-checkr-modal'
import t from './strings'

type SubmitMailingAddressProps = {
  uniqueLink: string
  mailingAddress?: {
    street: string
    streetLine2: string
    state: string
    city: string
    postalCode: string
  }
  mailingAddressError?: string
  updateMailingAddress?: (any) => Promise<any>
  submitMailingAddress?: (
    uniqueLink,
    mailingAddress,
    application
  ) => Promise<any>
  isSubmitting?: boolean
  onBackButtonClick?: () => Promise<any>
  setDropshipping?: (string, boolean) => Promise<any>
  setSupplyAcquisitionMethod?: (string) => void
  trackDropshippingEvent?: (string, data?) => void
  trackGoBack?: (string) => void
  experiments?: object
  application?: any
  stripeCredentials?: any
  getStripeCredentials?: any
  getPaymentAccount?: any
  dasherId?: string
  updatePaymentAccount?: any
  paymentAccountId?: string
}

export class SubmitMailingAddress extends React.Component<
  SubmitMailingAddressProps
> {
  public static defaultProps = {
    application: {
      country: 'US',
    },
    mailingAddress: {
      street: '',
      streetLine2: '',
      state: '',
      city: '',
      postalCode: '',
    },
    updateMailingAddress: noop,
    submitMailingAddress: noop,
    isSubmitting: false,
    onBackButtonClick: noop,
    setDropshipping: noop,
    trackDropshippingEvent: noop,
    trackGoBack: noop,
  }

  public componentDidMount() {
    const {
      uniqueLink,
      setDropshipping,
      trackDropshippingEvent,
      dasherId,
      getPaymentAccount,
      experiments,
      paymentAccountId
    } = this.props

    const bankOnboardingExperiment = get(experiments, 'bank_onboarding', false)
    if (bankOnboardingExperiment && dasherId) {
      if(!paymentAccountId) {
        getPaymentAccount(dasherId)
      }
    }
    setDropshipping(uniqueLink, true)
    trackDropshippingEvent('DA_dxds_address_page')
  }

  public handleSubmitMailingAddress = async () => {
    const {
      uniqueLink,
      mailingAddress,
      application,
      paymentAccountId,
      submitMailingAddress,
      updatePaymentAccount,
    } = this.props

    if (paymentAccountId) {
      const payload = {
        address: {
          line1: mailingAddress.street,
          line2: mailingAddress.streetLine2,
          city: mailingAddress.city,
          postal_code: mailingAddress.postalCode,
          state: mailingAddress.state,
        },
      }
      await updatePaymentAccount(paymentAccountId, payload)
    }

    await submitMailingAddress(uniqueLink, mailingAddress, application)
  }

  public handleBackButton = () => {
    this.props.trackGoBack('mail_address')
    this.props.setSupplyAcquisitionMethod('')
  }

  public shouldRenderBackButton = () => {
    const { experiments } = this.props
    const dropshippingSkipSelection = get(
      experiments,
      'dropshipping_skip_selection',
      false
    )

    // If user skipped over selection
    // page should not render a back button
    return !dropshippingSkipSelection
  }

  public render() {
    const {
      mailingAddress,
      mailingAddressError,
      updateMailingAddress,
      isSubmitting,
      application,
    } = this.props
    return (
      <div>
        {application.country === 'CA' &&
          application.backgroundCheckResults === 'incomplete' &&
          application.backgroundCheckCompletionUrl && <DDToCheckrModal />}
        <Stack size={Stack.Sizes.XLarge}>
          {this.handleBackButton && this.shouldRenderBackButton() && (
            <Button
              accessibilityDescribedBy={'backButton'}
              onClick={this.handleBackButton}
              type={Button.Types.TertiaryPill}
              size={Button.Sizes.Small}
              isInline
            >
              {`< ${t.back()}`}
            </Button>
          )}
          <Stack>
            <Text styles={Text.Styles.Bold24}>{t.h1()}</Text>
          </Stack>
          <Text size={Text.Sizes.Small} color={Text.Colors.Tertiary}>
            {t.instructionsOne()}
          </Text>
          <Text size={Text.Sizes.Small} color={Text.Colors.Tertiary}>
            {t.instructionsTwo()}
          </Text>
        </Stack>
        <Stack size={Stack.Sizes.Large}>
          <StackChildren>
            <TextField
              accessibilityDescribedBy={'shippingAddressLineOne'}
              value={mailingAddress.street}
              onChange={value => updateMailingAddress({ street: value })}
              label={t.streetAddress()}
              autoComplete="shipping address-line1"
              autoFocus={true}
            />
            <TextField
              accessibilityDescribedBy={'shippingAddressLineTwo'}
              value={mailingAddress.streetLine2}
              onChange={value => updateMailingAddress({ streetLine2: value })}
              label={t.streetAddressTwo()}
              autoComplete="shipping address-line2"
            />
            <TextField
              accessibilityDescribedBy={'city'}
              value={mailingAddress.city}
              onChange={value => updateMailingAddress({ city: value })}
              label={t.city()}
              autoComplete="shipping address-level2"
            />
            <Select
              accessibilityDescribedBy={'state'}
              label={application.country === 'CA' ? t.province() : t.state()}
              value={mailingAddress.state || ''}
              disabledValue={
                application.country === 'CA'
                  ? t.selectProvince()
                  : t.selectState()
              }
              onChange={value => updateMailingAddress({ state: value })}
              options={
                application.country === 'CA'
                  ? CANADA_PROVINCE_CODES
                  : STATE_CODES
              }
              name="state"
              autoComplete="shipping address-level1"
            />
            <TextField
              accessibilityDescribedBy={'postalCode'}
              value={mailingAddress.postalCode}
              onChange={value => updateMailingAddress({ postalCode: value })}
              label={t.zipcode()}
              autoComplete="shipping postal-code"
            />
          </StackChildren>
        </Stack>
        {mailingAddressError && (
          <Stack>
            <ErrorMessage error={mailingAddressError} />
          </Stack>
        )}
        <Button
          accessibilityDescribedBy={'submitAddress'}
          onClick={this.handleSubmitMailingAddress}
          state={isSubmitting ? Button.States.Loading : Button.States.Default}
          isDisabled={
            !mailingAddress.postalCode ||
            !mailingAddress.state ||
            !mailingAddress.city ||
            !mailingAddress.street
          }
        >
          {t.submitButton()}
        </Button>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  mailingAddress: state.mailingAddress,
  application: state.application,
  isSubmitting: isSubmittingAddressSelector(state),
  experiments: state.experiments,
  dasherId: state.application.dasherId,
  paymentAccountId: state.paymentAccount.id,
})

const mapDispatchToProps = {
  updateMailingAddress: actions.updateMailingAddress,
  submitMailingAddress: actions.submitMailingAddress,
  setDropshipping: actions.setDropshipping,
  setSupplyAcquisitionMethod: actions.setSupplyAcquisitionMethod,
  trackDropshippingEvent: events.trackDropshippingEvent,
  trackGoBack: events.trackGoBack,
  getPaymentAccount: actions.getPaymentAccount,
  updatePaymentAccount: actions.updatePaymentAccount,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SubmitMailingAddress)
